import { useMemo, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { EChart } from "@/components/charts/EChart";
import { ReportBreadcrumbs } from "@/components/report/ReportBreadcrumbs";
import { ReportContextSummary } from "@/components/report/ReportContextSummary";
import { ReportExportPanel } from "@/components/report/ReportExportPanel";
import { FloatingReportLayout } from "@/components/report/FloatingReportLayout";
import { ReportSourceError } from "@/components/report/ReportSourceError";
import {
  PrintableLineChart,
  PrintableReportDocument,
  PrintableReportHeader,
  PrintableReportSection,
} from "@/components/report/PrintableReport";
import { useReportAnalysisPoints } from "@/hooks/useReportAnalysis";
import { useStudentAssessmentsQuery, useStudentQuery } from "@/hooks/useStudents";
import { useCatalog } from "@/providers/CatalogProvider";
import type { Assessment, Student } from "@/types/assessment";
import type { DomainCode } from "@/constants/domains";
import { formatDate } from "@/utils/date";
import {
  formatPercent,
  formatScore,
  areAssessmentsComparable,
  renderAssessmentStatus,
  renderAssessmentType,
  resolveComparableAssessmentIds,
  type AssessmentAnalysisPoint,
  type ReportDomainStat,
} from "@/utils/report-analysis";
import { completedAssessmentsForStudent } from "@/utils/student-workspace";

const MAX_COMPARE_COUNT = 5;
const COMPARE_TOC_ITEMS = [
  { id: "compare-total", label: "总分趋势" },
  { id: "compare-domains", label: "领域对比" },
  { id: "compare-speed", label: "发展速度" },
  { id: "compare-insights", label: "观察与建议" },
];

export function ReportsComparePage(): JSX.Element {
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedDomain, setExpandedDomain] = useState<DomainCode | "">("HE");
  const studentQuery = useStudentQuery(studentId);
  const assessmentsQuery = useStudentAssessmentsQuery(studentId);
  const assessmentOptions = completedAssessmentsForStudent(
    assessmentsQuery.data ?? [],
    studentId,
  ).filter((assessment) => assessment.integrityStatus === "verified");
  const requestedAssessmentIds = parseAssessmentIds(searchParams.get("assessments"));
  const hasInvalidRequestedId = requestedAssessmentIds.some(
    (id) => !assessmentOptions.some((assessment) => assessment.id === id),
  );
  const selectedAssessmentIds = resolveSelectedAssessmentIds(
    searchParams.get("assessments"),
    assessmentOptions,
  );
  const selectedAssessments = assessmentOptions.filter((assessment) =>
    selectedAssessmentIds.includes(assessment.id),
  );
  const comparisonReference = selectedAssessments[0];
  const { scaleLibrary } = useCatalog();
  const analysis = useReportAnalysisPoints(selectedAssessments, scaleLibrary);
  const points = analysis.points;
  const firstPoint = points[0];
  const latestPoint = points[points.length - 1];
  const activeDomain =
    expandedDomain ||
    latestPoint?.domainStats.find((domain) => domain.completed > 0)?.code ||
    latestPoint?.domainStats[0]?.code ||
    "HE";
  const domainRows = useMemo(() => buildDomainRows(points), [points]);
  const trendSegments = useMemo(() => buildTrendSegments(points), [points]);
  const observations = useMemo(() => buildObservations(points), [points]);
  const recommendations = useMemo(
    () => buildRecommendations(latestPoint?.domainStats ?? []),
    [latestPoint],
  );
  const totalGrowth =
    firstPoint && latestPoint ? latestPoint.totalScore - firstPoint.totalScore : 0;
  const averageGrowth = points.length > 1 ? totalGrowth / (points.length - 1) : 0;

  const totalTrendOption = useMemo(() => {
    const maxScore = Math.max(...points.map((item) => item.totalScore), 10);
    return {
      title: { text: "总分趋势", left: "center", textStyle: { fontSize: 16 } },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: points.map((item) => formatDate(item.assessment.startedAt)),
      },
      yAxis: { type: "value", min: 0, max: Math.ceil(maxScore * 1.2) },
      series: [
        {
          type: "line",
          data: points.map((item) => Number(item.totalScore.toFixed(2))),
          smooth: true,
          symbolSize: 8,
          areaStyle: { opacity: 0.1 },
        },
      ],
    };
  }, [points]);

  const domainTrendOption = useMemo(() => {
    const activeDomainRows = points.map((point) =>
      point.domainStats.find((domain) => domain.code === activeDomain),
    );
    return {
      title: {
        text: `${activeDomainRows[0]?.name ?? "领域"}平均分变化`,
        left: "center",
        textStyle: { fontSize: 16 },
      },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: points.map((item) => formatDate(item.assessment.startedAt)),
      },
      yAxis: { type: "value", min: 0, max: 2 },
      series: [
        {
          type: "line",
          data: activeDomainRows.map((domain) => Number((domain?.average ?? 0).toFixed(2))),
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
        },
      ],
    };
  }, [activeDomain, points]);

  function setAssessmentSelection(ids: string[]): void {
    const next = new URLSearchParams(searchParams);
    if (ids.length > 0) {
      next.set("assessments", ids.slice(0, MAX_COMPARE_COUNT).join(","));
    } else {
      next.delete("assessments");
    }
    next.delete("view");
    setSearchParams(next);
  }

  function toggleSelection(id: string): void {
    const exists = selectedAssessmentIds.includes(id);
    if (exists) {
      setAssessmentSelection(selectedAssessmentIds.filter((item) => item !== id));
      return;
    }
    if (selectedAssessmentIds.length >= MAX_COMPARE_COUNT) {
      return;
    }
    const assessment = assessmentOptions.find((item) => item.id === id);
    if (
      !assessment ||
      (comparisonReference && !areAssessmentsComparable(comparisonReference, assessment))
    ) {
      return;
    }
    setAssessmentSelection([...selectedAssessmentIds, id]);
  }

  const handleDownloadPdf = () => window.print();
  const isViewing = searchParams.get("view") === "1" && selectedAssessmentIds.length >= 2;
  const selectionParams = new URLSearchParams();
  if (selectedAssessmentIds.length > 0) {
    selectionParams.set("assessments", selectedAssessmentIds.join(","));
  }

  function openReport(): void {
    const next = new URLSearchParams(selectionParams);
    next.set("view", "1");
    setSearchParams(next);
  }

  if (hasInvalidRequestedId) {
    return (
      <ReportSourceError
        studentId={studentId}
        title="对比记录不属于当前儿童"
        description="对比范围包含不可用或属于其他儿童的评估记录，请返回后重新选择。"
      />
    );
  }

  if (!isViewing) {
    return (
      <div className="space-y-6">
        <header>
          <ReportBreadcrumbs currentPage="评估对比报告" studentId={studentId} />
          <h1 className="text-2xl font-semibold text-slate-800">选择对比记录</h1>
          <p className="mt-2 text-sm text-slate-500">
            选择 2 至 {MAX_COMPARE_COUNT} 份同条目集合的评估，再生成固定范围的对比报告。
          </p>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="mt-1 text-base font-semibold text-slate-800">选择评估记录</h2>
              </div>
              <p className="text-sm text-slate-500">
                已选 {selectedAssessmentIds.length} / {MAX_COMPARE_COUNT} 份
              </p>
            </div>

            {assessmentsQuery.isLoading ? (
              <p className="mt-3 text-sm text-slate-500">正在加载评估记录…</p>
            ) : null}
            {assessmentsQuery.isError ? (
              <p className="mt-3 text-sm text-red-500">评估记录加载失败，请稍后重试。</p>
            ) : null}

            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {assessmentOptions.map((assessment) => (
                <AssessmentChoice
                  key={assessment.id}
                  assessment={assessment}
                  checked={selectedAssessmentIds.includes(assessment.id)}
                  disabled={
                    !selectedAssessmentIds.includes(assessment.id) &&
                    (selectedAssessmentIds.length >= MAX_COMPARE_COUNT ||
                      (comparisonReference !== undefined &&
                        !areAssessmentsComparable(comparisonReference, assessment)))
                  }
                  onChange={() => toggleSelection(assessment.id)}
                />
              ))}
            </div>

            {assessmentOptions.length === 0 && !assessmentsQuery.isLoading ? (
              <p className="mt-3 text-sm text-slate-500">该儿童暂无可用评估记录。</p>
            ) : null}
          </div>

          <div className="mt-6 flex items-center justify-between gap-4 border-t border-slate-200 pt-5">
            <p className="text-sm text-slate-500">至少选择两份可比记录。</p>
            <button
              type="button"
              onClick={openReport}
              disabled={selectedAssessmentIds.length < 2}
              className="min-h-11 rounded-md bg-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              生成对比报告
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 print:hidden">
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <ReportBreadcrumbs currentPage="评估对比报告" studentId={studentId} />
            <h1 className="text-2xl font-semibold text-slate-800">评估对比分析</h1>
            <p className="mt-2 text-sm text-slate-500">对比所选评估记录的阶段变化。</p>
          </div>
          <ReportExportPanel
            onDownloadPdf={handleDownloadPdf}
            disabled={points.length < 2 || analysis.isLoading}
          />
        </header>

        <ReportContextSummary
          studentName={studentQuery.data?.profile.name ?? "未知儿童"}
          assessmentLabels={selectedAssessments.map(
            (assessment) =>
              `${formatDate(assessment.startedAt)} · ${renderAssessmentType(assessment.type)}`,
          )}
        />

        {analysis.isLoading ? <p className="text-sm text-slate-500">正在汇总评分条目…</p> : null}
        {analysis.isError ? (
          <p className="text-sm text-red-500">
            正式报告加载失败，请确认所选记录属于同一儿童、目录版本与条目集合。
          </p>
        ) : null}

        {points.length < 2 ? (
          <section className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
            请选择至少两份评估记录生成对比报告。
          </section>
        ) : (
          <FloatingReportLayout items={COMPARE_TOC_ITEMS}>
            <section
              id="compare-total"
              className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-slate-700">总分趋势图</h2>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <EChart option={totalTrendOption} height={320} />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <TrendStat
                  title="区间变化"
                  value={`${totalGrowth >= 0 ? "+" : ""}${formatScore(totalGrowth)} 分`}
                  remark={`${formatDate(firstPoint?.assessment.startedAt)} 至 ${formatDate(
                    latestPoint?.assessment.startedAt,
                  )}`}
                />
                <TrendStat
                  title="平均每次变化"
                  value={`${averageGrowth >= 0 ? "+" : ""}${formatScore(averageGrowth)} 分`}
                  remark={`共 ${points.length} 次评估`}
                />
                <TrendStat
                  title="最新实得分"
                  value={`${formatScore(latestPoint?.totalScore ?? 0)} 分`}
                  remark={`完成率 ${formatPercent(latestPoint?.totalCompletionPercent ?? 0)}`}
                />
              </div>
            </section>

            <section
              id="compare-domains"
              className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-700">各领域得分对比</h2>
                <span className="text-xs text-slate-500">展开查看子领域变化</span>
              </div>
              <DomainComparisonTable rows={domainRows} points={points} />

              <div className="space-y-4">
                {latestPoint?.domainStats.map((domain) => (
                  <DomainPanel
                    key={domain.code}
                    title={`${domain.name} · 子领域对比`}
                    isOpen={activeDomain === domain.code}
                    onToggle={() =>
                      setExpandedDomain(activeDomain === domain.code ? "" : domain.code)
                    }
                  >
                    <div className="grid gap-4 lg:grid-cols-[2fr,3fr]">
                      <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                        <EChart option={domainTrendOption} height={260} />
                      </div>
                      <SubdomainComparisonTable domainCode={domain.code} points={points} />
                    </div>
                  </DomainPanel>
                ))}
              </div>
            </section>

            <section
              id="compare-speed"
              className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-slate-700">发展速度分析</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">时间段</th>
                      <th className="px-4 py-2 text-left font-medium">总分变化</th>
                      <th className="px-4 py-2 text-left font-medium">完成条目变化</th>
                      <th className="px-4 py-2 text-left font-medium">说明</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 text-slate-700">
                    {trendSegments.map((segment) => (
                      <tr key={segment.key}>
                        <td className="px-4 py-2">{segment.range}</td>
                        <td className="px-4 py-2">
                          {segment.scoreChange >= 0 ? "+" : ""}
                          {formatScore(segment.scoreChange)}
                        </td>
                        <td className="px-4 py-2">
                          {segment.completedChange >= 0 ? "+" : ""}
                          {segment.completedChange}
                        </td>
                        <td className="px-4 py-2">{segment.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section
              id="compare-insights"
              className="scroll-mt-[11.5rem] grid gap-4 lg:grid-cols-2"
            >
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-700">真实观察记录</h3>
                {observations.length > 0 ? (
                  <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-slate-600">
                    {observations.map((item, index) => (
                      <li key={`${index}-${item}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-4 text-sm text-slate-500">选中评估暂未保存条目备注。</p>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-3">
                <h3 className="text-lg font-semibold text-slate-700">对比建议</h3>
                <ul className="list-disc space-y-2 pl-5 text-sm text-slate-600">
                  {recommendations.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>
          </FloatingReportLayout>
        )}
      </div>
      {points.length >= 2 ? (
        <ComparePrintableDocument
          student={studentQuery.data}
          assessments={selectedAssessments}
          points={points}
          domainRows={domainRows}
          trendSegments={trendSegments}
          observations={observations}
          recommendations={recommendations}
          totalGrowth={totalGrowth}
          averageGrowth={averageGrowth}
        />
      ) : null}
    </>
  );
}

function ComparePrintableDocument({
  student,
  assessments,
  points,
  domainRows,
  trendSegments,
  observations,
  recommendations,
  totalGrowth,
  averageGrowth,
}: {
  student: Student | undefined;
  assessments: Assessment[];
  points: AssessmentAnalysisPoint[];
  domainRows: DomainComparisonRow[];
  trendSegments: ReturnType<typeof buildTrendSegments>;
  observations: string[];
  recommendations: string[];
  totalGrowth: number;
  averageGrowth: number;
}): JSX.Element {
  const firstPoint = points[0];
  const latestPoint = points[points.length - 1];
  const dateLabels = points.map((point) => formatDate(point.assessment.startedAt));

  return (
    <PrintableReportDocument ariaLabel="评估对比分析打印文档">
      <PrintableReportHeader
        eyebrow="阶段变化"
        title="评估对比分析报告"
        description="对比所选评估记录的得分、领域表现与发展变化。"
        student={student}
        assessmentLabel={assessments
          .map(
            (assessment) =>
              `${formatDate(assessment.startedAt)} · ${renderAssessmentType(assessment.type)}`,
          )
          .join("、")}
      />

      <PrintableReportSection title="一、总分趋势">
        <div className="report-print-chart report-print-block">
          <PrintableLineChart
            ariaLabel="评估总分趋势图"
            labels={dateLabels}
            values={points.map((point) => point.totalScore)}
          />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <PrintStat
            title="区间变化"
            value={`${totalGrowth >= 0 ? "+" : ""}${formatScore(totalGrowth)} 分`}
            remark={`${formatDate(firstPoint.assessment.startedAt)} 至 ${formatDate(latestPoint.assessment.startedAt)}`}
          />
          <PrintStat
            title="平均每次变化"
            value={`${averageGrowth >= 0 ? "+" : ""}${formatScore(averageGrowth)} 分`}
            remark={`共 ${points.length} 次评估`}
          />
          <PrintStat
            title="最新实得分"
            value={`${formatScore(latestPoint.totalScore)} 分`}
            remark={`完成率 ${formatPercent(latestPoint.totalCompletionPercent)}`}
          />
        </div>
      </PrintableReportSection>

      <PrintableReportSection title="二、各领域得分对比">
        <div className="report-print-block overflow-hidden rounded-xl border border-slate-200">
          <table className="report-print-table report-print-table-compact">
            <thead>
              <tr>
                <th>领域</th>
                {dateLabels.map((label, index) => (
                  <th key={`${label}-${points[index].assessment.id}`}>{label}</th>
                ))}
                <th>变化</th>
              </tr>
            </thead>
            <tbody>
              {domainRows.map((row) => (
                <tr key={row.domain.code}>
                  <td className="font-semibold">{row.domain.name}</td>
                  {row.values.map((value, index) => (
                    <td key={`${row.domain.code}-${points[index].assessment.id}`}>
                      {formatScore(value.score)}
                      <span className="block text-[8.5pt] text-slate-500">
                        平均 {value.average === null ? "—" : formatScore(value.average)}
                      </span>
                    </td>
                  ))}
                  <td className={row.change >= 0 ? "text-emerald-700" : "text-red-600"}>
                    {row.change >= 0 ? "+" : ""}
                    {formatScore(row.change)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 space-y-5">
          {latestPoint.domainStats.map((domain) => {
            const subdomains = latestPoint.subdomainStats.filter(
              (subdomain) => subdomain.domainCode === domain.code,
            );
            return (
              <section
                key={domain.code}
                className="report-print-domain report-print-block rounded-xl border border-slate-200 p-4"
              >
                <h3 className="text-[12pt] font-semibold text-slate-800">
                  {domain.name} · 子领域对比
                </h3>
                <div className="report-print-chart mt-3">
                  <PrintableLineChart
                    ariaLabel={`${domain.name}平均分变化图`}
                    labels={dateLabels}
                    values={points.map(
                      (point) =>
                        point.domainStats.find((item) => item.code === domain.code)?.average ?? 0,
                    )}
                    max={2}
                  />
                </div>
                <table className="report-print-table report-print-table-compact mt-3">
                  <thead>
                    <tr>
                      <th>子领域</th>
                      {dateLabels.map((label, index) => (
                        <th key={`${domain.code}-${label}-${points[index].assessment.id}`}>
                          {label}
                        </th>
                      ))}
                      <th>完成率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subdomains.map((subdomain) => (
                      <tr key={subdomain.key}>
                        <td>{subdomain.label}</td>
                        {points.map((point) => {
                          const stat = point.subdomainStats.find(
                            (item) => item.key === subdomain.key,
                          );
                          return (
                            <td key={`${point.assessment.id}-${subdomain.key}`}>
                              {stat?.average == null ? "—" : formatScore(stat.average)}
                            </td>
                          );
                        })}
                        <td>{formatPercent(subdomain.completionPercent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })}
        </div>
      </PrintableReportSection>

      <PrintableReportSection title="三、发展速度分析">
        <div className="report-print-block overflow-hidden rounded-xl border border-slate-200">
          <table className="report-print-table">
            <thead>
              <tr>
                <th>时间段</th>
                <th>总分变化</th>
                <th>完成条目变化</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              {trendSegments.map((segment) => (
                <tr key={segment.key}>
                  <td>{segment.range}</td>
                  <td>
                    {segment.scoreChange >= 0 ? "+" : ""}
                    {formatScore(segment.scoreChange)}
                  </td>
                  <td>
                    {segment.completedChange >= 0 ? "+" : ""}
                    {segment.completedChange}
                  </td>
                  <td>{segment.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PrintableReportSection>

      <PrintableReportSection title="四、观察与建议">
        <div className="grid grid-cols-2 gap-4">
          <section className="report-print-block rounded-xl border border-slate-200 p-4">
            <h3 className="text-[11pt] font-semibold text-slate-800">真实观察记录</h3>
            <PrintBulletList items={observations} />
          </section>
          <section className="report-print-block rounded-xl border border-brand/25 bg-brand/5 p-4">
            <h3 className="text-[11pt] font-semibold text-slate-800">对比建议</h3>
            <PrintBulletList items={recommendations} />
          </section>
        </div>
      </PrintableReportSection>
    </PrintableReportDocument>
  );
}

function PrintStat({ title, value, remark }: { title: string; value: string; remark: string }) {
  return (
    <section className="report-print-block rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-[9pt] text-slate-500">{title}</p>
      <p className="mt-1 text-[16pt] font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-[9pt] text-slate-500">{remark}</p>
    </section>
  );
}

function PrintBulletList({ items }: { items: string[] }): JSX.Element {
  return items.length > 0 ? (
    <ul className="mt-3 list-disc space-y-2 pl-5 text-[10.5pt] leading-6 text-slate-700">
      {items.map((item, index) => (
        <li key={`${index}-${item}`}>{item}</li>
      ))}
    </ul>
  ) : (
    <p className="mt-3 text-[10.5pt] text-slate-500">暂无记录</p>
  );
}

function AssessmentChoice({
  assessment,
  checked,
  disabled,
  onChange,
}: {
  assessment: Assessment;
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}): JSX.Element {
  const inputId = `compare-assessment-${assessment.id}`;
  return (
    <label
      htmlFor={inputId}
      className={`flex cursor-pointer items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
        checked
          ? "border-brand bg-brand/10 text-brand"
          : "border-slate-200 bg-slate-50 text-slate-600"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <span>
        <span className="block font-semibold">
          {formatDate(assessment.startedAt)} · {renderAssessmentType(assessment.type)}
        </span>
        <span className="text-xs text-slate-500">
          {renderAssessmentStatus(assessment.status)} ·{" "}
          {assessment.evaluatorDisplayName ?? "历史评估师"}
        </span>
      </span>
      <input
        id={inputId}
        type="checkbox"
        aria-label={`选择评估记录 ${formatDate(assessment.startedAt)} ${renderAssessmentType(assessment.type)}`}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
      />
    </label>
  );
}

function TrendStat({
  title,
  value,
  remark,
}: {
  title: string;
  value: string;
  remark: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs uppercase tracking-wide text-slate-400">{title}</p>
      <p className="mt-2 text-xl font-semibold text-slate-800">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{remark}</p>
    </div>
  );
}

function DomainComparisonTable({
  rows,
  points,
}: {
  rows: DomainComparisonRow[];
  points: AssessmentAnalysisPoint[];
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left font-medium">领域</th>
            {points.map((point) => (
              <th key={point.assessment.id} className="px-4 py-2 text-left font-medium">
                {formatDate(point.assessment.startedAt)}
              </th>
            ))}
            <th className="px-4 py-2 text-left font-medium">变化</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 text-slate-700">
          {rows.map((row) => (
            <tr key={row.domain.code}>
              <td className="px-4 py-2 font-semibold">{row.domain.name}</td>
              {row.values.map((value, index) => (
                <td key={`${row.domain.code}-${points[index].assessment.id}`} className="px-4 py-2">
                  <div className="flex min-w-[120px] items-center gap-2">
                    <span>{formatScore(value.score)}</span>
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{ width: `${Math.round(value.scorePercent * 100)}%` }}
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    平均 {value.average === null ? "-" : formatScore(value.average)}
                  </p>
                </td>
              ))}
              <td
                className={
                  row.change >= 0 ? "px-4 py-2 text-emerald-600" : "px-4 py-2 text-red-500"
                }
              >
                {row.change >= 0 ? "+" : ""}
                {formatScore(row.change)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubdomainComparisonTable({
  domainCode,
  points,
}: {
  domainCode: DomainCode;
  points: AssessmentAnalysisPoint[];
}): JSX.Element {
  const latest = points[points.length - 1];
  const subdomains = latest?.subdomainStats.filter((item) => item.domainCode === domainCode) ?? [];
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
      <table className="w-full text-xs text-slate-600">
        <thead>
          <tr>
            <th className="py-1 text-left font-medium">子领域</th>
            {points.map((point) => (
              <th key={point.assessment.id} className="py-1 text-center font-medium">
                {formatDate(point.assessment.startedAt)}
              </th>
            ))}
            <th className="py-1 text-center font-medium">完成率</th>
          </tr>
        </thead>
        <tbody>
          {subdomains.map((subdomain) => (
            <tr key={subdomain.key}>
              <td className="py-1 pr-2 text-slate-700">{subdomain.label}</td>
              {points.map((point) => {
                const stat = point.subdomainStats.find((item) => item.key === subdomain.key);
                return (
                  <td key={`${point.assessment.id}-${subdomain.key}`} className="py-1 text-center">
                    {stat?.average === null || stat?.average === undefined
                      ? "-"
                      : formatScore(stat.average)}
                  </td>
                );
              })}
              <td className="py-1 text-center">{formatPercent(subdomain.completionPercent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DomainPanel({
  title,
  children,
  isOpen,
  onToggle,
}: {
  title: string;
  children: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-3 text-left text-sm font-semibold text-slate-700"
      >
        <span>{title}</span>
        <span>{isOpen ? "▲" : "▼"}</span>
      </button>
      {isOpen ? <div className="border-t border-slate-100 p-5">{children}</div> : null}
    </div>
  );
}

interface DomainComparisonRow {
  domain: ReportDomainStat;
  values: ReportDomainStat[];
  change: number;
}

function buildDomainRows(points: AssessmentAnalysisPoint[]): DomainComparisonRow[] {
  const latest = points[points.length - 1];
  if (!latest) {
    return [];
  }
  return latest.domainStats.map((domain) => {
    const values = points.map(
      (point) => point.domainStats.find((item) => item.code === domain.code) ?? domain,
    );
    return {
      domain,
      values,
      change: values[values.length - 1].score - values[0].score,
    };
  });
}

function resolveSelectedAssessmentIds(param: string | null, assessments: Assessment[]): string[] {
  if (assessments.length === 0) {
    return [];
  }
  if (param === null) {
    return [];
  }
  const ids = parseAssessmentIds(param);
  return resolveComparableAssessmentIds(ids, assessments, MAX_COMPARE_COUNT);
}

function parseAssessmentIds(param: string | null): string[] {
  return (param ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildTrendSegments(points: AssessmentAnalysisPoint[]): Array<{
  key: string;
  range: string;
  scoreChange: number;
  completedChange: number;
  description: string;
}> {
  return points.slice(1).map((point, index) => {
    const previous = points[index];
    const scoreChange = point.totalScore - previous.totalScore;
    const completedChange = point.totalCompleted - previous.totalCompleted;
    const strongestDomain = findStrongestGrowingDomain(previous, point);
    return {
      key: `${previous.assessment.id}-${point.assessment.id}`,
      range: `${formatDate(previous.assessment.startedAt)} - ${formatDate(point.assessment.startedAt)}`,
      scoreChange,
      completedChange,
      description: strongestDomain
        ? `${strongestDomain.name}变化 ${strongestDomain.change >= 0 ? "+" : ""}${formatScore(
            strongestDomain.change,
          )} 分。`
        : "暂无领域得分变化。",
    };
  });
}

function findStrongestGrowingDomain(
  previous: AssessmentAnalysisPoint,
  current: AssessmentAnalysisPoint,
): { name: string; change: number } | null {
  const changes = current.domainStats.map((domain) => {
    const previousDomain = previous.domainStats.find((item) => item.code === domain.code);
    return {
      name: domain.name,
      change: domain.score - (previousDomain?.score ?? 0),
    };
  });
  return changes.sort((a, b) => Math.abs(b.change) - Math.abs(a.change))[0] ?? null;
}

function buildObservations(points: AssessmentAnalysisPoint[]): string[] {
  return points
    .flatMap((point) =>
      point.observations.map(
        (observation) =>
          `${formatDate(point.assessment.startedAt)} · ${observation.entryCode}：${observation.comment}`,
      ),
    )
    .slice(0, 8);
}

function buildRecommendations(domains: ReportDomainStat[]): string[] {
  const scored = domains.filter((domain) => domain.completed > 0);
  if (scored.length === 0) {
    return ["暂无已评分条目，完成评估后可生成对比建议。"];
  }
  const weakest = [...scored].sort((a, b) => (a.average ?? 0) - (b.average ?? 0))[0];
  const strongest = [...scored].sort((a, b) => (b.average ?? 0) - (a.average ?? 0))[0];
  return [
    `${strongest.name}当前平均分 ${formatScore(strongest.average ?? 0)}，可作为后续活动设计中的优势入口。`,
    `${weakest.name}当前平均分 ${formatScore(weakest.average ?? 0)}，建议优先回看该领域低分条目的观察备注和短期目标。`,
    "当前结果只比较同一儿童、同目录版本和同条目集合的记录，评估方式可以不同。",
  ];
}
